import { useState, useEffect, useCallback } from 'react';
import { TeamView } from '../../../services/TeamService';

interface ValidationErrors {
  name?: string;
  description?: string;
}

/**
 * Custom hook for team form validation.
 * Tracks touched fields so errors only surface after user interaction.
 */
export const useTeamFormValidation = (team: TeamView | null) => {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isValid, setIsValid] = useState(false);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());

  // Validate team form data
  const validateForm = useCallback((): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    if (!team) {
      newErrors.name = 'Team data is required';
      newErrors.description = 'Team data is required';
      return newErrors;
    }

    // Team name validation
    if (!team.name || team.name.trim().length === 0) {
      newErrors.name = 'Team name is required';
    } else if (team.name.trim().length < 2) {
      newErrors.name = 'Team name must be at least 2 characters';
    } else if (team.name.trim().length > 100) {
      newErrors.name = 'Team name cannot exceed 100 characters';
    }

    // Team description validation
    if (!team.description || team.description.trim().length === 0) {
      newErrors.description = 'Team description is required';
    } else if (team.description.trim().length < 10) {
      newErrors.description = 'Team description must be at least 10 characters';
    } else if (team.description.trim().length > 500) {
      newErrors.description = 'Team description cannot exceed 500 characters';
    }

    return newErrors;
  }, [team]);

  // Run validation whenever team data changes
  useEffect(() => {
    const validationErrors = validateForm();
    setErrors(validationErrors);
    setIsValid(Object.keys(validationErrors).length === 0);
  }, [team?.name, team?.description, validateForm]);

  // Mark a single field as touched (call on blur)
  const touchField = useCallback((fieldName: string) => {
    setTouchedFields(prev => new Set(prev).add(fieldName));
  }, []);

  // Mark all fields as touched (call before save to surface remaining errors)
  const touchAll = useCallback(() => {
    setTouchedFields(new Set(['name', 'description']));
  }, []);

  // Get error message for a specific field (only when touched)
  const getFieldError = (fieldName: keyof ValidationErrors): string | undefined => {
    return touchedFields.has(fieldName) ? errors[fieldName] : undefined;
  };

  // Check if a specific field has an error (only when touched)
  const hasFieldError = (fieldName: keyof ValidationErrors): boolean => {
    return touchedFields.has(fieldName) && !!errors[fieldName];
  };

  // Get all validation errors (raw, regardless of touched state)
  const getAllErrors = (): ValidationErrors => errors;

  // Check if form is valid for submission
  const canSubmit = (): boolean => {
    return isValid && team !== null && 
           team.name?.trim().length > 0 && 
           team.description?.trim().length > 0;
  };

  return {
    errors,
    isValid,
    getFieldError,
    hasFieldError,
    getAllErrors,
    canSubmit,
    touchField,
    touchAll,
  };
};

export default useTeamFormValidation;